/**
 * The one-phone game, as a pure reducer. No networking: this is the v1
 * experience, unchanged, and it must keep working with the server switched off.
 *
 * `setup → handoff → clue → handoff → guess (×N) → reveal → …`
 */

import type { Card, DeckId } from '@shared/content';
import { deckFor } from '@shared/content';
import {
  giverPointsFor,
  pointsFor,
  quantise,
  randomTarget,
  shuffle,
} from '@shared/scoring';

export type LocalScreen = 'setup' | 'handoff' | 'clue' | 'guess' | 'reveal' | 'final';

export interface LocalPlayer {
  name: string;
  score: number;
}

export interface LocalGuess {
  playerIdx: number;
  value: number;
  points: number;
}

export interface LocalState {
  screen: LocalScreen;
  deckId: DeckId;
  players: LocalPlayer[];
  passes: 1 | 2;
  totalRounds: number;
  round: number;
  deck: Card[];
  deckPos: number;
  card: Card | null;
  target: number;
  clue: string;
  giverIdx: number;
  /** Guesser indices for this round, in seating order after the giver. */
  queue: number[];
  queuePos: number;
  guesses: LocalGuess[];
  giverPoints: number;
  /** Rounds that actually scored — the co-op ceiling is derived from this. */
  scoredRounds: number;
  /** Who the pending handoff is addressed to, and where it leads. */
  handoff: { toIdx: number; next: 'clue' | 'guess'; sub: string } | null;
}

export type LocalAction =
  | { t: 'start'; names: string[]; passes: 1 | 2; deck: DeckId }
  | { t: 'handoffDone' }
  | { t: 'submitClue'; text: string }
  | { t: 'submitGuess'; value: number }
  | { t: 'nextRound' }
  | { t: 'endGame' }
  | { t: 'playAgain' };

export function emptyLocalState(): LocalState {
  return {
    screen: 'setup',
    deckId: 'normal',
    players: [],
    passes: 1,
    totalRounds: 0,
    round: 0,
    deck: [],
    deckPos: 0,
    card: null,
    target: 50,
    clue: '',
    giverIdx: 0,
    queue: [],
    queuePos: 0,
    guesses: [],
    giverPoints: 0,
    scoredRounds: 0,
    handoff: null,
  };
}

/** Deal the next card, reshuffling only when the deck is exhausted (§3). */
function draw(state: LocalState): { deck: Card[]; deckPos: number; card: Card } {
  let { deck, deckPos } = state;
  if (deckPos >= deck.length) {
    deck = shuffle(deckFor(state.deckId));
    deckPos = 0;
  }
  return { deck, deckPos: deckPos + 1, card: deck[deckPos] };
}

function beginRound(state: LocalState, round: number): LocalState {
  const giverIdx = round % state.players.length;
  const { deck, deckPos, card } = draw(state);
  const queue: number[] = [];
  for (let i = 1; i < state.players.length; i++) {
    queue.push((giverIdx + i) % state.players.length);
  }
  return {
    ...state,
    round,
    giverIdx,
    deck,
    deckPos,
    card,
    target: randomTarget(),
    clue: '',
    queue,
    queuePos: 0,
    guesses: [],
    giverPoints: 0,
    screen: 'handoff',
    handoff: {
      toIdx: giverIdx,
      next: 'clue',
      sub: 'You write the clue this round. Everyone else looks away.',
    },
  };
}

export function localReducer(state: LocalState, action: LocalAction): LocalState {
  switch (action.t) {
    case 'start': {
      const players = action.names.map((name) => ({ name, score: 0 }));
      const base: LocalState = {
        ...emptyLocalState(),
        players,
        deckId: action.deck,
        passes: action.passes,
        totalRounds: players.length * action.passes,
        deck: shuffle(deckFor(action.deck)),
        deckPos: 0,
      };
      return beginRound(base, 0);
    }

    case 'handoffDone': {
      if (!state.handoff) return state;
      return { ...state, screen: state.handoff.next, handoff: null };
    }

    case 'submitClue': {
      const text = action.text.trim();
      if (!text) return state;
      const toIdx = state.queue[0];
      return {
        ...state,
        clue: text,
        screen: 'handoff',
        queuePos: 0,
        handoff: {
          toIdx,
          next: 'guess',
          sub: 'Your turn to guess. Nobody else looks.',
        },
      };
    }

    case 'submitGuess': {
      const playerIdx = state.queue[state.queuePos];
      const value = quantise(action.value);
      const guesses = [
        ...state.guesses,
        { playerIdx, value, points: pointsFor(value, state.target) },
      ];
      const nextPos = state.queuePos + 1;

      if (nextPos < state.queue.length) {
        return {
          ...state,
          guesses,
          queuePos: nextPos,
          screen: 'handoff',
          handoff: {
            toIdx: state.queue[nextPos],
            next: 'guess',
            sub: 'Your turn to guess. Nobody else looks.',
          },
        };
      }

      // Last guess in: score the round.
      const giverPoints = giverPointsFor(guesses.map((g) => g.points));
      const players = state.players.map((p, i) => {
        if (i === state.giverIdx) return { ...p, score: p.score + giverPoints };
        const g = guesses.find((x) => x.playerIdx === i);
        return g ? { ...p, score: p.score + g.points } : p;
      });
      return {
        ...state,
        guesses,
        players,
        giverPoints,
        scoredRounds: state.scoredRounds + (guesses.length > 0 ? 1 : 0),
        screen: 'reveal',
      };
    }

    case 'nextRound': {
      const next = state.round + 1;
      if (next >= state.totalRounds) return { ...state, screen: 'final' };
      return beginRound(state, next);
    }

    case 'endGame':
      return { ...state, screen: 'final' };

    case 'playAgain': {
      const base: LocalState = {
        ...emptyLocalState(),
        players: state.players.map((p) => ({ ...p, score: 0 })),
        deckId: state.deckId,
        passes: state.passes,
        totalRounds: state.players.length * state.passes,
        deck: shuffle(deckFor(state.deckId)),
        deckPos: 0,
      };
      return beginRound(base, 0);
    }

    default:
      return state;
  }
}

/** Ranked standings, highest first. Ties keep seating order. */
export function standings(players: LocalPlayer[]): { idx: number; player: LocalPlayer }[] {
  return players
    .map((player, idx) => ({ player, idx }))
    .sort((a, b) => b.player.score - a.player.score || a.idx - b.idx);
}

/** Winner names — plural when it's a tie. */
export function winners(players: LocalPlayer[]): string[] {
  const top = Math.max(...players.map((p) => p.score));
  return players.filter((p) => p.score === top).map((p) => p.name);
}
